/**
 * Session Heartbeat Hook
 * 
 * Keeps long-running sessions (like 24-hour practice mode) alive by:
 * 1. Sending periodic heartbeat pings to the server
 * 2. Detecting browser sleep/wake and refreshing session immediately
 * 3. Handling token refresh proactively for uninterrupted sessions
 */

import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from '../store';
import { refreshToken } from '../store/authSlice';
import authClient from '../api/authClient';

// Heartbeat interval: 5 minutes
const HEARTBEAT_INTERVAL_MS = 5 * 60 * 1000;

// Minimum time since last activity before sending heartbeat
const MIN_ACTIVITY_THRESHOLD_MS = 30 * 1000; // 30 seconds

// Sleep detection threshold: if time jump is > 10 seconds, assume device was asleep
const SLEEP_DETECTION_THRESHOLD_MS = 10 * 1000;

interface UseSessionHeartbeatOptions {
  /** Whether heartbeat is enabled (default: true when authenticated) */
  enabled?: boolean;
  /** Custom heartbeat interval in ms (default: 5 minutes) */
  intervalMs?: number;
  /** Callback when session is about to expire */
  onSessionExpiring?: () => void;
  /** Callback when heartbeat fails */
  onHeartbeatFailed?: (error: Error) => void;
}

interface HeartbeatState {
  lastHeartbeat: number;
  lastActivity: number;
  isActive: boolean;
}

export const useSessionHeartbeat = (options: UseSessionHeartbeatOptions = {}) => {
  const {
    enabled = true,
    intervalMs = HEARTBEAT_INTERVAL_MS,
    onSessionExpiring,
    onHeartbeatFailed,
  } = options;

  const dispatch = useAppDispatch();
  const { isAuthenticated, tokenExpiresAt } = useAppSelector((state) => state.auth);
  
  const stateRef = useRef<HeartbeatState>({
    lastHeartbeat: Date.now(),
    lastActivity: Date.now(),
    isActive: true,
  });
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastTickRef = useRef<number>(Date.now());

  /**
   * Send heartbeat to server
   * This keeps the session alive on the backend
   */
  const sendHeartbeat = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      // Send a lightweight ping to keep session alive
      // The backend should have a /auth/heartbeat or similar endpoint
      // For now, we'll use /auth/me as a lightweight session check
      await authClient.get('/auth/me');
      
      stateRef.current.lastHeartbeat = Date.now();
      console.log('💓 [SessionHeartbeat] Heartbeat sent successfully');
    } catch (error) {
      console.error('❌ [SessionHeartbeat] Heartbeat failed:', error);
      onHeartbeatFailed?.(error as Error);
    }
  }, [isAuthenticated, onHeartbeatFailed]);

  /**
   * Check if token is expiring soon and refresh proactively
   */
  const checkTokenExpiry = useCallback(async () => {
    if (!tokenExpiresAt) return;

    const now = Date.now();
    const timeUntilExpiry = tokenExpiresAt - now;
    const TEN_MINUTES = 10 * 60 * 1000;
    const FIVE_MINUTES = 5 * 60 * 1000;

    // Warn when < 10 minutes remaining
    if (timeUntilExpiry < TEN_MINUTES && timeUntilExpiry > FIVE_MINUTES) {
      console.log('⚠️ [SessionHeartbeat] Token expiring in < 10 minutes');
      onSessionExpiring?.();
    }

    // Refresh when < 5 minutes remaining
    if (timeUntilExpiry < FIVE_MINUTES && timeUntilExpiry > 0) {
      console.log('🔄 [SessionHeartbeat] Proactively refreshing token...');
      try {
        await dispatch(refreshToken()).unwrap();
        console.log('✅ [SessionHeartbeat] Token refreshed successfully');
      } catch (error) {
        console.error('❌ [SessionHeartbeat] Token refresh failed:', error);
      }
    }
  }, [tokenExpiresAt, dispatch, onSessionExpiring]);

  /**
   * Detect browser sleep/wake by checking time jumps
   */
  const detectSleepWake = useCallback(() => {
    const now = Date.now();
    const timeSinceLastTick = now - lastTickRef.current;
    
    if (timeSinceLastTick > SLEEP_DETECTION_THRESHOLD_MS + intervalMs) {
      console.log('😴 [SessionHeartbeat] Device wake detected, sending immediate heartbeat');
      
      // Device was likely asleep, send immediate heartbeat and check token
      sendHeartbeat();
      checkTokenExpiry();
    }
    
    lastTickRef.current = now;
  }, [intervalMs, sendHeartbeat, checkTokenExpiry]);

  /**
   * Track user activity to avoid unnecessary heartbeats
   */
  const trackActivity = useCallback(() => {
    stateRef.current.lastActivity = Date.now();
    stateRef.current.isActive = true;
  }, []);

  /**
   * Main heartbeat tick function
   */
  const heartbeatTick = useCallback(() => {
    // Check for sleep/wake
    detectSleepWake();
    
    // Check token expiry
    checkTokenExpiry();
    
    // Only send heartbeat if there was recent activity
    const timeSinceLastActivity = Date.now() - stateRef.current.lastActivity;
    if (timeSinceLastActivity < MIN_ACTIVITY_THRESHOLD_MS || stateRef.current.isActive) {
      sendHeartbeat();
      stateRef.current.isActive = false; // Reset activity flag
    }
  }, [detectSleepWake, checkTokenExpiry, sendHeartbeat]);

  // Set up heartbeat interval
  useEffect(() => {
    if (!enabled || !isAuthenticated) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Initial heartbeat
    sendHeartbeat();
    
    // Set up periodic heartbeat
    intervalRef.current = setInterval(heartbeatTick, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isAuthenticated, intervalMs, heartbeatTick, sendHeartbeat]);

  // Track user activity events
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    
    events.forEach((event) => {
      document.addEventListener(event, trackActivity, { passive: true });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, trackActivity);
      });
    };
  }, [enabled, isAuthenticated, trackActivity]);

  // Handle visibility change (tab focus/blur)
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('👁️ [SessionHeartbeat] Tab became visible, checking session...');
        detectSleepWake();
        checkTokenExpiry();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, isAuthenticated, detectSleepWake, checkTokenExpiry]);

  // Handle online/offline events
  useEffect(() => {
    if (!enabled || !isAuthenticated) return;

    const handleOnline = () => {
      console.log('🌐 [SessionHeartbeat] Network reconnected, sending heartbeat...');
      sendHeartbeat();
      checkTokenExpiry();
    };

    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [enabled, isAuthenticated, sendHeartbeat, checkTokenExpiry]);

  return {
    /** Manually trigger a heartbeat */
    sendHeartbeat,
    /** Check and refresh token if needed */
    checkTokenExpiry,
    /** Last successful heartbeat timestamp */
    lastHeartbeat: stateRef.current.lastHeartbeat,
  };
};

export default useSessionHeartbeat;

